/* eslint-disable @typescript-eslint/no-non-null-assertion */

import type { RecipeMainIngredient } from '@apps/main/modules/recipe/ingredients';
import type {
  ExternalErrorQueryParamValue,
  CustomerOriginQueryParamValue,
  ModalQueryParamValue,
} from '@apps/main/new-desktop/core/models';

import { GraphQL } from '@libs/trip-planner-interfaces';
import type {
  Pax,
  AvailabilityOrder,
  Landing,
} from '@libs/trip-planner-interfaces';
import {
  DateSerializer,
  DummySerializer,
  BooleanSerializer,
  IntSerializer,
  isEnumValue,
} from '@libs/utils';
import type { ConnectionType } from '@libs/maps';

import { ConnectionTypeSerializer } from '../../serializer/connectionTypeSerializer';
import { IngredientSerializer } from '../../serializer/ingredientSerializer/legacy';
import { ModalSerializer } from '../../serializer/modalSerializer';
import { ExternalErrorSerializer } from '../../serializer/externalErrorSerializer';
import { CustomerOriginSerializer } from '../../serializer/customerOriginSerializer';
import { PaxSerializer } from '../../serializer/paxSerializer';
import { RecipeSerializer } from '../../serializer/recipeSerializer';
import { SalesChannelSerializer } from '../../serializer/salesChannelSerializer';

import type { TripPlannerQueryString, TripPlannerProperties } from './model';
import type { QueryMapping } from './router';

const ingredientIdPattern = /\bi\d+\b/g;

export function parseRecipe(
  query: QueryMapping<TripPlannerQueryString, TripPlannerProperties>
): QueryMapping<TripPlannerQueryString, TripPlannerProperties> {
  return query
    .property('recipe', RecipeSerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: () => undefined!,
      setter: (value: TripPlannerProperties, props: TripPlannerProperties) => {
        props.initialIngredients = value.initialIngredients;
        props.initialPax = value.initialPax;
        props.initialSelectedDate = value.initialSelectedDate;
        props.initialSelectedMonth = value.initialSelectedMonth
          ? value.initialSelectedMonth - 1
          : undefined;
        props.initialSuggestionSortType = value.initialSuggestionSortType;
        props.sessionId = value.sessionId;
        props.metaData = value.metaData;
        props.searchForPackageRates = value.searchForPackageRates;
      },
      hasDefaultValue: (value: TripPlannerProperties) => !value,
    })
    .property('i', IngredientSerializer)
    .array()
    .with({
      getter: (queryString: TripPlannerQueryString) => queryString.ingredients,
      setter: (value: RecipeMainIngredient[], props: TripPlannerProperties) =>
        (props.initialIngredients = value),
      hasDefaultValue: (value: RecipeMainIngredient[]) => !value,
    })
    .propertyPattern(ingredientIdPattern, IngredientSerializer)
    .array()
    .with({
      getter: (queryString: TripPlannerQueryString) => queryString.ingredients,
      setter: (value: RecipeMainIngredient[], props: TripPlannerProperties) =>
        (props.initialIngredients = value),
      hasDefaultValue: (value: RecipeMainIngredient[]) => !value,
    })
    .property('pax', PaxSerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) => queryString.pax,
      setter: (value: Pax, props: TripPlannerProperties) =>
        (props.initialPax = value),
    })
    .property('day', DateSerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) =>
        queryString.selectedDate!,
      setter: (value: Date, props: TripPlannerProperties) =>
        (props.initialSelectedDate = value),
      hasDefaultValue: (value: Date) => !value,
    })
    .property('suggest', DummySerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) => queryString.suggestedBy!,
      setter: (value: string, props: TripPlannerProperties) =>
        (props.initialSuggestionSortType = value as AvailabilityOrder),
      hasDefaultValue: (value: string) => !value,
    })
    .property('landing', DummySerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) => queryString.landing!,
      setter: (value: string, props: TripPlannerProperties) =>
        (props.landing = value as Landing),
      hasDefaultValue: (value: string) => !value,
    })
    .property('rt', ConnectionTypeSerializer)
    .with({
      getter: (queryString: TripPlannerQueryString) =>
        queryString.returnTransport!,
      setter: (value: ConnectionType, props: TripPlannerProperties) =>
        (props.returnTransport = value),
      hasDefaultValue: (value: ConnectionType) => !value,
    })
    .property('meta-room', DummySerializer)
    .array()
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) => queryString.metaRooms!,
      setter: (value: string[], props: TripPlannerProperties) =>
        (props.initialMetaRooms = value),
      hasDefaultValue: (value: string[]) => !value || !value.length,
    })
    .property('redirectionToken', DummySerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) =>
        queryString.redirectionToken!,
      setter: (value: string, props: TripPlannerProperties) =>
        (props.initialRedirectionToken = value),
      hasDefaultValue: (value: string) => !value,
    })
    .property('m', IntSerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) =>
        queryString.selectedMonth! + 1,
      setter: (value: number, props: TripPlannerProperties) =>
        (props.initialSelectedMonth = value - 1),
      hasDefaultValue: (value: number) => !value,
    })
    .property('ai-trip-id', DummySerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) => queryString.aiTripId!,
      setter: (value: string, props: TripPlannerProperties) =>
        (props.aiTripId = value),
      hasDefaultValue: (value: string) => !value,
    })
    .property('published-trip-id', DummySerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) =>
        queryString.publishedTripId!,
      setter: (value: string, props: TripPlannerProperties) =>
        (props.publishedTripId = value),
      hasDefaultValue: (value: string) => !value,
    })
    .property('modal', ModalSerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) => queryString.modal!,
      setter: (value: ModalQueryParamValue, props: TripPlannerProperties) =>
        (props.modal = value),
      hasDefaultValue: (value: string) => !value,
    })
    .property('external-error', ExternalErrorSerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) =>
        queryString.externalError!,
      setter: (
        value: ExternalErrorQueryParamValue,
        props: TripPlannerProperties
      ) => (props.externalError = value),
      hasDefaultValue: (value: string) => !value,
    })
    .property('customer-origin', CustomerOriginSerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) =>
        queryString.customerOrigin!,
      setter: (
        value: CustomerOriginQueryParamValue,
        props: TripPlannerProperties
      ) => (props.customerOrigin = value),
      hasDefaultValue: (value: string) => !value,
    })
    .property('searchForPackageRates', BooleanSerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) =>
        queryString.searchForPackageRates!,
      setter: (value: boolean, props: TripPlannerProperties) =>
        (props.searchForPackageRates = value),
      hasDefaultValue: (value: boolean) => !value,
    })
    .property('sales-channel', SalesChannelSerializer)
    .ignoreEmptyQueryParameters()
    .with({
      getter: (queryString: TripPlannerQueryString) =>
        queryString.salesChannel &&
        isEnumValue(GraphQL.InputSaleChannelQL, queryString.salesChannel)
          ? queryString.salesChannel
          : null,
      setter: (value, props: TripPlannerProperties) =>
        (props.metaData = {
          ...props.metaData,
          salesChannel: value,
        }),
      hasDefaultValue: (value) => !value,
    });
}
